import type { JSXElementConstructor } from 'react';
import AccordionFull from 'components/AccordionFull/AccordionFull';
import AnimatedMarquee from 'components/AnimatedMarquee/AnimatedMarquee';
import ArticleGrid from '@/components/ArticleGrid/ArticleGrid';
import BentoBox from 'components/BentoBox/BentoBox/BentoBox';
import CalloutGrid from 'components/CalloutGrid/CalloutGrid';
import CalloutIcon from 'components/Callouts/CalloutIcon/CalloutIcon';
import CalloutImage from 'components/Callouts/CalloutImage/CalloutImage';
import CanvasNav from 'components/CanvasNav/CanvasNav';
import CardGrid from 'components/CardGrid/CardGrid';
import CardCarousel from 'components/CardCarousel/CardCarousel';
import CardsExpandable from 'components/Cards/CardsExpandable/CardsExpandable';
import CardsGridComplex from 'components/Cards/CardsGridComplex/CardsGridComplex';
import CardsGridSimple from 'components/Cards/CardsGridSimple/CardsGridSimple';
import CardsIconCarousel from 'components/Cards/CardsIcon/CardsIconCarousel';
import CardsImageCarousel from 'components/Cards/CardsImage/CardsImageCarousel';
import CardsStacked from 'components/CardsStacked/CardsStacked';
import CardsVerticalScroll from '@/components/CardsVerticalScroll/CardsVerticalScroll';
import Compare from 'components/Compare/Compare';
import ContentBlock from 'components/ContentBlock/ContentBlock';
import ContentFeatureCarousel from '@/components/ContentFeatureCarousel/ContentFeatureCarousel';
import CrossSell from 'components/shared/CrossSell/CrossSell';
import CtaAddOn from 'components/CtaAddOn/CtaAddOn';
import DisclosureAddOn from 'components/DisclosureAddOn/DisclosureAddOn';
import DisclosureBanner from 'components/DisclosureBanner/DisclosureBanner';
import DisclosureCollection from 'components/DisclosureCollection/DisclosureCollection';
import DisclosureGroupings from 'components/DisclosureGroupings/DisclosureGroupings';
import Explainer from 'components/Explainer/Explainer';
import Faq from 'components/Faq/Faq';
import FeaturedBanner from 'components/FeaturedBanner/FeaturedBanner';
import FeaturedJobs from 'components/FeaturedJobs/FeaturedJobs';
import Features from 'components/Features/Features';
import FullBleedImageHeadline from '@/components/FullBleedImageHeadline/FullBleedImageHeadline';
import FullWidthImage from '@/components/FullWidthImage/FullWidthImage';
import HeaderCta from 'components/HeaderCTA/HeaderCta';
import HeaderLogo from 'components/HeaderLogo/HeaderLogo';
import HeadlineWithEyebrow from '@/components/HeadlineWithEyebrow/HeadlineWithEyebrow';
import HeroConsolidated from 'components/HeroConsolidated/HeroConsolidated';
import HomepageHero from 'components/HomepageHero/HomepageHero';
import HubSpot from 'components/HubSpot/HubSpot';
import ImpactHeadline from 'components/ImpactHeadline/ImpactHeadline';
import KeyLine from 'components/Keyline/Keyline';
import Launchpad from 'components/Launchpad/Launchpad';
import MapLocator from 'components/MapLocator/MapLocator';
import MediaCopy from 'components/MediaCopy/MediaCopy';
import MediaCopyDivided from 'components/MediaCopyDivided/MediaCopyDivided';
import Nav from 'components/MainNav/Nav';
import Note from 'components/Note/Note';
// Import the shell (children-only wrapper) — not the full PageSection — to
// avoid the PageSection → ModuleCollectionRenderer → ModuleRenderer →
// moduleRegistry cycle. The v2 pipeline materializes children upstream.
import PageSection from 'components/PageSection/PageSectionShell';
import Resources from 'components/Resources/Resources';
import SectionBanner from 'components/SectionBanner/SectionBanner';
import SectionDisclosures from 'components/SectionDisclosures/SectionDisclosures';
import SectionFooter from 'components/SectionFooter/SectionFooter';
import SectionHero from 'components/SectionHero/SectionHero';
import SectionNavigation from 'components/SectionNavigation/SectionNavigation';
import SiteFooter from 'components/Footer/SiteFooter';
import Subnav from 'components/Subnav/Subnav';
import TabbedMediaCopy from 'components/TabbedMediaCopy/TabbedMediaCopy';
import TeaserLinks from 'components/TeaserLinks/TeaserLinks';
import Testimonial from 'components/Testimonial/Testimonial';
import TextEditor from 'components/TextEditor/TextEditor';
import type { EnrichedModuleByTypeName, RenderableModuleTypeName } from './propInjection';

/**
 * All renderable __typename values in the module registry.
 */
export type ModuleRegistryTypeName = RenderableModuleTypeName;

type ModuleRegistry = {
  [K in ModuleRegistryTypeName]: JSXElementConstructor<EnrichedModuleByTypeName<K>>;
};

/**
 * Unified module registry mapping every renderable __typename to its React component.
 * Single source of truth for module dispatch.
 *
 * For PageSection we import PageSectionShell (a children-only wrapper) rather than
 * the full PageSection. The full component imports ModuleCollectionRenderer, which
 * transitively re-imports moduleRegistry, producing an init-time cycle that trips
 * the TDZ when PageSection is the bundle entry (e.g. ClientPageSection in client
 * mode). The renderer always pre-renders children for PageSection, so the shell
 * is sufficient.
 */
export const moduleRegistry: ModuleRegistry = {
  Accordion: AccordionFull,
  AnimatedMarquee: AnimatedMarquee,
  ArticleGrid: ArticleGrid,
  BentoBox: BentoBox,
  CalloutGrid: CalloutGrid,
  CalloutImage: CalloutImage,
  CalloutIcon: CalloutIcon,
  CanvasNav: CanvasNav,
  CardGrid: CardGrid,
  CardCarousel: CardCarousel,
  CardsExpandable: CardsExpandable,
  CardsGridComplex: CardsGridComplex,
  CardsGridSimple: CardsGridSimple,
  CardsIcon: CardsIconCarousel,
  CardsImage: CardsImageCarousel,
  CardsStacked: CardsStacked,
  CardsVerticalScroll: CardsVerticalScroll,
  Compare: Compare,
  ContentBlock: ContentBlock,
  ContentFeatureCarousel: ContentFeatureCarousel,
  CopyAddOnWithButton: CtaAddOn,
  CrossSell: CrossSell,
  DisclosureAddOn: DisclosureAddOn,
  DisclosureBanner: DisclosureBanner,
  DisclosureCollection: DisclosureCollection,
  DisclosureGroupings: DisclosureGroupings,
  Explainer: Explainer,
  Faq: Faq,
  FeaturedBanner: FeaturedBanner,
  FeaturedJobs: FeaturedJobs,
  Features: Features,
  FullBleedImageHeadline: FullBleedImageHeadline,
  FullWidthImage: FullWidthImage,
  HeaderCta: HeaderCta,
  HeaderLogo: HeaderLogo,
  HeadlineWithEyebrow: HeadlineWithEyebrow,
  HeroConsolidated: HeroConsolidated,
  HomepageHero: HomepageHero,
  HubSpot: HubSpot,
  ImpactHeadline: ImpactHeadline,
  Keyline: KeyLine,
  Launchpad: Launchpad,
  MapLocator: MapLocator,
  MediaCopy: MediaCopy,
  MediaCopyDivided: MediaCopyDivided,
  Nav: Nav,
  Note: Note,
  PageSection: PageSection,
  ResourcesItgBranded: Resources,
  RichTextEditor: TextEditor,
  SectionBanner: SectionBanner,
  SectionDisclosures: SectionDisclosures,
  SectionFooter: SectionFooter,
  SectionHero: SectionHero,
  SectionNavigation: SectionNavigation,
  SiteFooter: SiteFooter,
  Subnav: Subnav,
  TabbedMediaCopy: TabbedMediaCopy,
  TeaserLinks: TeaserLinks,
  Testimonial: Testimonial,
  TextEditor: TextEditor,
};

export function isModuleRegistryTypeName(value: string): value is ModuleRegistryTypeName {
  return Object.prototype.hasOwnProperty.call(moduleRegistry, value);
}
